import { Injectable } from '@angular/core';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FirebaseanalyticsService {
  private isWebInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    this.initializeFirebase();
  }

  /**
   * Inicializa Firebase Analytics para plataforma web
   */
  private initializeFirebase(): void {
    if (Capacitor.getPlatform() === 'web') {
      this.initializationPromise = this.initializeFirebaseWeb();
    } else {
      // En plataformas nativas, no necesitamos inicialización adicional
      this.isWebInitialized = true;
    }
  }

  /**
   * Verifica si las credenciales de Firebase están configuradas correctamente
   */
  private isFirebaseConfigured(): boolean {
    if (!environment.firebase) {
      return false;
    }
    
    // Verificar que no sean los valores por defecto (placeholders)
    const config = environment.firebase;
    return config.apiKey !== 'YOUR_API_KEY' && 
           config.projectId !== 'YOUR_PROJECT_ID' &&
           config.apiKey.length > 0 &&
           config.projectId.length > 0;
  }

  /**
   * Inicializa Firebase para web
   */
  private async initializeFirebaseWeb(): Promise<void> {
    if (this.isWebInitialized) {
      return;
    }

    try {
      // Verificar si las credenciales están configuradas
      if (!this.isFirebaseConfigured()) {
        console.warn('⚠️ Firebase Analytics: Las credenciales no están configuradas correctamente.');
        console.warn('📝 Por favor, actualiza los valores en src/environments/environment.ts');
        console.warn('📚 Consulta FIREBASE_ANALYTICS_SETUP.md para más información');
        this.isWebInitialized = true; // Marcar como inicializado para evitar reintentos
        return;
      }

      // Importar e inicializar Firebase para web
      const { initializeApp, getApps } = await import('firebase/app');
      const { getAnalytics, isSupported } = await import('firebase/analytics');
      
      // Verificar si ya existe una instancia de Firebase
      if (getApps().length === 0) {
        // Verificar si Analytics está soportado en el navegador
        const supported = await isSupported();
        if (supported) {
          initializeApp(environment.firebase);
          getAnalytics();
          console.log('✅ Firebase Analytics inicializado para web');
          this.isWebInitialized = true;
        } else {
          console.warn('⚠️ Firebase Analytics no está soportado en este navegador');
          this.isWebInitialized = true; // Marcar como inicializado para evitar reintentos
        }
      } else {
        // Ya existe una app de Firebase
        this.isWebInitialized = true;
        console.log('✅ Firebase ya está inicializado');
      }
    } catch (error) {
      console.error('❌ Error al inicializar Firebase Analytics en web:', error);
      this.isWebInitialized = true; // Marcar como inicializado para evitar que bloquee la app
    }
  }

  /**
   * Espera a que Firebase esté inicializado (solo para web)
   */
  private async ensureInitialized(): Promise<void> {
    if (Capacitor.getPlatform() === 'web' && this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  /**
   * Habilita o deshabilita Firebase Analytics
   * @param enable - true para habilitar, false para deshabilitar
   */
  async setEnabledFirebaseAnalytics(enable: boolean = true): Promise<void> {
    try {
      await this.ensureInitialized();
      await FirebaseAnalytics.setEnabled({ enabled: enable });
      console.log(`Firebase Analytics ${enable ? 'habilitado' : 'deshabilitado'}`);
    } catch (error) {
      console.error('Error al habilitar/deshabilitar Analytics:', error);
    }
  }

  /**
   * Registra el nombre de la pantalla actual
   * @param screenName - Nombre de la pantalla
   * @param screenClass - Clase de la pantalla (opcional)
   */
  async setScreenName(screenName: string, screenClass?: string): Promise<void> {
    try {
      await this.ensureInitialized();
      await FirebaseAnalytics.setCurrentScreen({
        screenName: screenName,
        screenClassOverride: screenClass || screenName
      });
      console.log(`Screen view registrado: ${screenName}`);
    } catch (error) {
      console.error('Error al registrar screen view:', error);
    }
  }

  /**
   * Alias para mantener compatibilidad con código anterior
   * @param titlePage - Título de la página
   */
  async screenView(titlePage: string): Promise<void> {
    return this.setScreenName(titlePage);
  }

  /**
   * Registra un evento personalizado
   * @param name - Nombre del evento
   * @param params - Parámetros del evento
   */
  async logEvent(name: string, params?: any): Promise<void> {
    try {
      await this.ensureInitialized();
      await FirebaseAnalytics.logEvent({
        name: name,
        params: params || {}
      });
      console.log(`Evento registrado: ${name}`, params);
    } catch (error) {
      console.error('Error al registrar evento:', error);
    }
  }

  /**
   * Establece el ID del usuario
   * @param userId - ID único del usuario
   */
  async setUserId(userId: string): Promise<void> {
    try {
      await this.ensureInitialized();
      await FirebaseAnalytics.setUserId({ userId: userId });
      console.log(`User ID establecido: ${userId}`);
    } catch (error) {
      console.error('Error al establecer User ID:', error);
    }
  }

  /**
   * Establece una propiedad de usuario
   * @param name - Nombre de la propiedad
   * @param value - Valor de la propiedad
   */
  async setUserProperty(name: string, value: string): Promise<void> {
    try {
      await this.ensureInitialized();
      await FirebaseAnalytics.setUserProperty({
        key: name,
        value: value
      });
      console.log(`User property establecida: ${name} = ${value}`);
    } catch (error) {
      console.error('Error al establecer user property:', error);
    }
  }

  /**
   * Registra un evento de login
   * @param method - Método de login (email, google, facebook, etc.)
   */
  async logLogin(method: string): Promise<void> {
    return this.logEvent('login', { method: method });
  }

  /**
   * Registra un evento de signup
   * @param method - Método de registro
   */
  async logSignUp(method: string): Promise<void> {
    return this.logEvent('sign_up', { method: method });
  }

  /**
   * Registra un evento de búsqueda
   * @param searchTerm - Término de búsqueda
   */
  async logSearch(searchTerm: string): Promise<void> {
    return this.logEvent('search', { search_term: searchTerm });
  }

  /**
   * Registra un evento personalizado de compartir
   * @param contentType - Tipo de contenido compartido
   * @param itemId - ID del item compartido
   */
  async logShare(contentType: string, itemId: string): Promise<void> {
    return this.logEvent('share', { 
      content_type: contentType,
      item_id: itemId 
    });
  }
}
